import { useState, useEffect, useCallback, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import {
  konamiActiveAtom, genesisBlockAtom, pizzaDayAtom,
  lightningModeAtom, milestoneAtom, satsGameAtom,
  satsModeAtom, explorerBlockAtom,
} from '../state/atoms';

const STORAGE_KEY = 'tesseract_achievements';

export const ACHIEVEMENTS = [
  // Easter eggs
  { id: 'konami', name: 'CODE BREAKER', description: 'Entered the Konami Code' },
  { id: 'genesis', name: 'IN THE BEGINNING', description: 'Visited the Genesis Block' },
  { id: 'pizza', name: 'LASZLO PIZZA', description: 'Celebrated Bitcoin Pizza Day' },
  { id: 'lightning', name: 'LIGHTNING ROUTES', description: 'Activated Lightning Mode' },
  { id: 'fire', name: 'BLOCK ANIMATOR', description: 'Triggered the fireworks' },
  { id: 'sats', name: 'SAT STACKER', description: 'Played Stack Sats Game' },
  { id: 'satsMode', name: 'FLIP THE TABLE', description: 'Toggled sats-per-dollar view' },
  { id: 'explorer', name: 'BLOCK EXPLORER', description: 'Flew into a block' },
  { id: 'insideCube', name: 'DOWN THE RABBIT HOLE', description: 'Flew inside the tesseract' },
  { id: 'whitepaper', name: 'REQUIRED READING', description: 'Opened the Bitcoin Whitepaper' },
  // Scene interaction
  { id: 'flight', name: 'FIRST FLIGHT', description: 'Used keyboard flight controls' },
  { id: 'zoom', name: 'ZOOM OUT', description: 'Zoomed with scroll wheel' },
  { id: 'mouseOrbit', name: 'ORBITAL MECHANICS', description: 'Rotated the view with mouse' },
  { id: 'deepSpace', name: 'MEMPOOL OBSERVER', description: 'Zoomed out to maximum distance' },
  { id: 'fullOrbit', name: 'FULL ORBIT', description: 'Rotated the scene 360 degrees' },
  // UI interactions
  { id: 'pause', name: 'TIME STOP', description: 'Paused the animation' },
  { id: 'fullscreen', name: 'FULL IMMERSION', description: 'Entered fullscreen mode' },
  { id: 'currency', name: 'GLOBE TROTTER', description: 'Switched to a different currency' },
  { id: 'speedMax', name: 'TO THE MOON', description: 'Set speed to maximum' },
  { id: 'unmute', name: 'LISTENING NODE', description: 'Unmuted the audio' },
  { id: 'resetView', name: 'HODL', description: 'Reset the camera view' },
];

const FLIGHT_KEYS = new Set([
  'KeyW', 'KeyA', 'KeyS', 'KeyD',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'KeyQ', 'KeyE', 'KeyR', 'KeyF', 'KeyZ', 'KeyX', 'Space',
]);

const loadAchievements = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const useAchievements = () => {
  const [achievements, setAchievements] = useState(loadAchievements);
  const [pending, setPending] = useState(null);
  const initialLoadRef = useRef(true);
  const achievementsRef = useRef(achievements);
  achievementsRef.current = achievements;

  const konamiActive = useRecoilValue(konamiActiveAtom);
  const genesisBlock = useRecoilValue(genesisBlockAtom);
  const pizzaDay = useRecoilValue(pizzaDayAtom);
  const lightningMode = useRecoilValue(lightningModeAtom);
  const milestone = useRecoilValue(milestoneAtom);
  const satsGame = useRecoilValue(satsGameAtom);
  const satsMode = useRecoilValue(satsModeAtom);
  const explorerBlock = useRecoilValue(explorerBlockAtom);

  const unlock = useCallback((id) => {
    setAchievements(prev => {
      if (prev[id]) return prev;
      const next = { ...prev, [id]: { unlockedAt: Date.now() } };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      const info = ACHIEVEMENTS.find(a => a.id === id);
      const count = Object.keys(next).length;
      setPending({ ...info, count, allComplete: count >= ACHIEVEMENTS.length });
      return next;
    });
  }, []);

  const clearPending = useCallback(() => setPending(null), []);

  // Skip atom-based unlocks on initial mount to avoid triggering from default states
  useEffect(() => {
    initialLoadRef.current = false;
  }, []);

  // Atom watchers — only fire after initial mount
  useEffect(() => {
    if (!initialLoadRef.current && konamiActive) unlock('konami');
  }, [konamiActive, unlock]);

  useEffect(() => {
    if (!initialLoadRef.current && genesisBlock) unlock('genesis');
  }, [genesisBlock, unlock]);

  useEffect(() => {
    if (!initialLoadRef.current && pizzaDay) unlock('pizza');
  }, [pizzaDay, unlock]);

  useEffect(() => {
    if (!initialLoadRef.current && lightningMode) unlock('lightning');
  }, [lightningMode, unlock]);

  useEffect(() => {
    if (!initialLoadRef.current && milestone) unlock('fire');
  }, [milestone, unlock]);

  useEffect(() => {
    if (!initialLoadRef.current && satsGame) unlock('sats');
  }, [satsGame, unlock]);

  useEffect(() => {
    if (!initialLoadRef.current && satsMode) unlock('satsMode');
  }, [satsMode, unlock]);

  useEffect(() => {
    if (!initialLoadRef.current && explorerBlock) unlock('explorer');
  }, [explorerBlock, unlock]);

  // Input-based achievements: flight keys, scroll zoom, mouse orbit
  useEffect(() => {
    const unlockRef = achievementsRef;
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (FLIGHT_KEYS.has(e.code) && !unlockRef.current.flight) unlock('flight');
    };
    const handleWheel = () => {
      if (!unlockRef.current.zoom) unlock('zoom');
    };
    let dragging = false;
    const handleDown = () => { dragging = true; };
    const handleMove = () => {
      if (dragging && !unlockRef.current.mouseOrbit) unlock('mouseOrbit');
    };
    const handleUp = () => { dragging = false; };

    window.addEventListener('keydown', handleKey);
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('pointerdown', handleDown);
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('pointerdown', handleDown);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [unlock]);

  const count = Object.keys(achievements).length;
  const allComplete = count >= ACHIEVEMENTS.length;

  return { achievements, unlock, pending, clearPending, count, allComplete };
};
